import React, {useState, useEffect, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import {validacion, numeros} from 'lib.common';
import {useBoolean} from '../../../hooks';

function FormulaField({
   value,
   onValueChange,
   formula,
   onFormulaChange,
   evaluar,
   evaluarParametros,
   error: errorProp,
   helperText: helperTextProp,
   name,
   cifrasEnteras,
   cifrasDecimales,
   puntoDecimal,
   separadorMiles,
   onFocus,
   onBlur,
   onRefChange = () => {},
   ...other
}) {
   const inputRef = useRef(null);
   //Funcion para generar el parametro "e" para onValueChange y onFormulaChange
   function evento(nuevoValor) {
      return {
         target: {
            name,
            value: nuevoValor
         }
      };
   }
   //Funcion de validacion de cadenas decimales dependiendo de las propiedades
   const validarDecimal = useCallback(() => {
      const cifrasEnterasParam = cifrasEnteras || 13;
      const cifrasDecimalesParam = cifrasDecimales || 2;
      return validacion.validadorCadenaDecimal(cifrasEnterasParam, cifrasDecimalesParam);
   }, [cifrasEnteras, cifrasDecimales]);
   //Funcion de formateo del componente
   const formatoNumeroDecimal = useCallback(
      (nro) => {
         return numeros.formatoNumeroDecimal(nro, {
            cifrasDecimales,
            puntoDecimal,
            separadorMiles
         });
      },
      [cifrasDecimales, puntoDecimal, separadorMiles]
   );
   //Ultimo valor
   //Manejo de errores
   const [helperText, setHelperText] = useState('');
   const [errorFlag, {setTrue, setFalse}] = useBoolean(false);
   function mostrarError(msg, err) {
      setTrue();
      setHelperText(msg);
      console.log(err);
   }
   function limpiarError() {
      setFalse();
      setHelperText('');
   }
   //Control del estado de edicion
   const [edit, setEdit] = useState(value.toString() || '');
   function handleOnChange(e) {
      if (e.target.value && /^-?\d+$/.test(e.target.value[0])) {
         if (validarDecimal(e.target.value)) {
            setEdit(e.target.value);
            onRefChange({
               ref: inputRef,
               onChange: handleOnChange,
               value: e.target.value,
               formula: formula,
               onFormulaChange: onFormulaChange,
               limpiarError: limpiarError
            });
         }
      } else {
         setEdit(e.target.value);
         onRefChange({
            ref: inputRef,
            onChange: handleOnChange,
            value: e.target.value,
            formula: formula,
            onFormulaChange: onFormulaChange,
            limpiarError: limpiarError
         });
      }
      limpiarError();
   }

   useEffect(() => {
      const newEdit = formatoNumeroDecimal(value);
      if (newEdit.formato !== edit) {
         setEdit(newEdit.formato);
      }
   }, [value]);

   //Controlar formula y value solo cuando se ha terminado de modificar el edit usando el evento onBlur
   function handleEvaluacion(e) {
      const newEdit = e.target.value;
      if (newEdit) {
         //Si la edicion es formula
         if (newEdit[0] === '=') {
            //controlar formula con el nuevo valor obtenido ingresado en el input
            if (onFormulaChange && newEdit !== formula) {
               onFormulaChange(evento(newEdit));
            } else {
               //si la formula no ha sido actualizada mostrar el value para visualizacion
               const oldFormato = formatoNumeroDecimal(value);
               setEdit(oldFormato.formato);
            }
         } else {
            //Controlar value si el input no es una formula
            if (onValueChange) {
               let newValue = newEdit;
               if (validarDecimal(newValue)) {
                  newValue = formatoNumeroDecimal(parseFloat(newValue));
               }
               if (value !== newValue.numero) {
                  if (isNaN(newValue.numero)) {
                     onValueChange(evento(0));
                  } else {
                     onValueChange(evento(newValue.numero));
                  }
               } else {
                  if (newValue.formato !== newEdit) {
                     setEdit(newValue.formato);
                  }
               }
            }
            //Reiniciar formula si su valor actual es diferente del vacio
            if (onFormulaChange && formula !== '') {
               onFormulaChange(evento(''));
            }
         }
      } else {
         //Controlar value y formula si el input ingresado es vacio
         if (onValueChange && !value) {
            onValueChange(evento(0));
         }
         if (onFormulaChange && formula !== '') {
            onFormulaChange(evento(''));
         }
      }
      if (onBlur) {
         onBlur(e);
      }
   }
   //Realizamos la evaluacion de la nueva formula
   function evaluarFormula() {
      if (evaluar && formula && formula[0] === '=') {
         try {
            let newValue = evaluar(formula.substring(1), evaluarParametros);
            //if (validarDecimal(newValue)) {
            let newFormato = formatoNumeroDecimal(newValue);
            //}
            if (onValueChange && value !== newFormato.numero) {
               onValueChange(evento(newFormato.numero));
            }
            limpiarError();
         } catch (error) {
            mostrarError('La formula ingresada no tiene el formato correcto.', error);
         }
      }
   }
   //Evaluar cuando la formula o los parametros cambien
   useEffect(evaluarFormula, [formula, evaluarParametros]);
   //Cambiar el valor mostrado al momento de editar por la formula si formula no esta vacia
   function handleOnFocus(e) {
      onRefChange({
         ref: inputRef,
         onChange: handleOnChange,
         value: edit,
         formula: formula,
         onFormulaChange: onFormulaChange,
         limpiarError: limpiarError
      });
      if (onFocus) {
         onFocus(e);
      }
      if (formula && formula[0] === '=') {
         setEdit(formula);
      } else {
         setEdit(value.toString());
      }
      if (Boolean(inputRef && inputRef.current)) {
         setTimeout(() => {
            inputRef.current.select();
         }, 0);
      }
   }
   /*
   //Habilitar la evaluacion de la formula si se presiona enter y formula no esta vacia
   function handleOnEnterPress(e) {
      if (e.key === 'Enter') {
         handleEvaluacion(e);
      }
   }
   //*/
   return (
      <TextField
         {...other}
         value={edit}
         onChange={handleOnChange}
         onBlur={handleEvaluacion}
         onFocus={handleOnFocus}
         //onKeyDown={handleOnEnterPress}
         error={(errorFlag || errorProp) && (Boolean(helperText) || Boolean(helperTextProp))}
         helperText={helperText || helperTextProp}
         name={name}
         inputRef={inputRef}
         style={{maxWidth: '206px'}}
      />
   );
}
FormulaField.propTypes = {
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onValueChange: PropTypes.func,
   formula: PropTypes.string,
   onFormulaChange: PropTypes.func,
   evaluar: PropTypes.func,
   evaluarParametros: PropTypes.object,
   error: PropTypes.any,
   helperText: PropTypes.string,
   cifrasEnteras: PropTypes.number,
   cifrasDecimales: PropTypes.number,
   puntoDecimal: PropTypes.bool,
   separadorMiles: PropTypes.string
};
FormulaField.defaultProps = {
   cifrasDecimales: 0,
   puntoDecimal: true,
   separadorMiles: ','
};
export default FormulaField;
